/**
 * WizardLayout — Generic step-based wizard container.
 *
 * Renders a step indicator bar, Back/Next/Cancel navigation,
 * and a content slot. Steps are passed as an ordered list —
 * no hardcoded step names.
 */

import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui';
import { ChevronLeft, ChevronRight, X, Check } from 'lucide-react';
import type { WizardStep } from '../../types';

interface WizardLayoutProps {
  steps: WizardStep[];
  currentStep: number;
  onNext: () => void;
  onBack: () => void;
  canAdvance: boolean;
  isSubmitting?: boolean;
  children: ReactNode;
}

const STEP_LABELS: Record<WizardStep, string> = {
  connect: 'datasources.wizard.steps.connect',
  browse: 'datasources.wizard.steps.browse',
  configure: 'datasources.wizard.steps.configure',
  review: 'datasources.wizard.steps.review',
};

export function WizardLayout({
  steps,
  currentStep,
  onNext,
  onBack,
  canAdvance,
  isSubmitting = false,
  children,
}: WizardLayoutProps) {
  const { t } = useI18n();
  const navigate = useNavigate();

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Step indicator */}
      <nav className="flex items-center justify-center gap-2 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        {steps.map((step, idx) => {
          const isActive = idx === currentStep;
          const isCompleted = idx < currentStep;
          return (
            <div key={step} className="flex items-center">
              {idx > 0 && (
                <div
                  className={`w-8 h-px mx-2 ${
                    isCompleted ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              )}
              <div className="flex items-center gap-2">
                <div
                  className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium transition-colors ${
                    isCompleted
                      ? 'bg-blue-500 text-white'
                      : isActive
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 ring-2 ring-blue-500'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  {isCompleted ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                </div>
                <span
                  className={`text-sm hidden sm:inline ${
                    isActive
                      ? 'text-gray-900 dark:text-white font-medium'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {t(STEP_LABELS[step])}
                </span>
              </div>
            </div>
          );
        })}
      </nav>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">{children}</div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <Button
          variant="ghost"
          onClick={() => navigate('/knowledge/datasources')}
          className="gap-1"
        >
          <X className="w-4 h-4" />
          {t('common.cancel')}
        </Button>

        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button variant="outline" onClick={onBack} className="gap-1">
              <ChevronLeft className="w-4 h-4" />
              {t('common.back')}
            </Button>
          )}
          <Button
            onClick={onNext}
            disabled={!canAdvance || isSubmitting}
            className="gap-1"
          >
            {isLast ? (
              <>
                {isSubmitting
                  ? t('datasources.wizard.creating')
                  : t('datasources.wizard.create')}
              </>
            ) : (
              <>
                {t('common.next')}
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
